// @ts-strict-ignore
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
    AppPlatform,
    AppStoreType,
    Integration,
    NextReleaseSummary,
    ReleaseStepStatus,
    ReleaseStepType,
    ReleaseType,
    StatisticType,
} from '../../types';
import { RollbackTag } from '@/components/appHome/RollbackTag';
import { AppComponentProps, AppRouteComponentMatchParams } from '../../typescriptHelpers';
import { ReleasePilotWidget } from '@/components/appHome/widgets/ReleasePilotWidget';
import { TargetDatesWidget } from '@/components/appHome/widgets/TargetDatesWidget';
import { ReleaseStepsWidget } from '@/components/appHome/widgets/ReleaseStepsWidget';
import { AppHomeCard } from '@/components/appHome/AppHomeCard';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import { Divider, Space, Tag } from 'antd/lib';
import { BuildSelectorComponent } from '@/components/sharedComponents/buildSelector/BuildSelectorComponent';
import { FeatureCompletionWidget } from '@/components/appHome/widgets/FeatureCompletionWidget';
import { ReleaseTimelineWidget } from '@/components/appHome/widgets/ReleaseTimelineWidget';
import { ReleaseDescriptionWidget } from '@/components/appHome/widgets/ReleaseDescriptionWidget';
import { dayjs } from '@/extendedDayjs';
import { ClockCircleOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { ReleaseMetricsWidget } from '@/components/appHome/widgets/ReleaseMetricsWidget';
import {
    selectReleaseStepsBlockingConfigForReleaseType,
    selectShouldHideNonBlockingSteps,
    selectIsWorkflowsCIStepEnabled,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { useSelector } from 'react-redux';

interface NextReleaseSummaryProps extends AppComponentProps {
    summary: NextReleaseSummary;
    platform: AppPlatform;
    appStoreType?: AppStoreType;
    stabilityIntegration?: Integration;
    appStoreIntegration?: Integration;
}

export const NextReleaseComponent: React.FC<NextReleaseSummaryProps> = (props) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appIdParam),
    );
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, appIdParam),
    );
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appIdParam),
    );

    const appStoreSelectedBuilds = props.summary.appStoreBuilds;

    const [selectedBuild, setSelectedBuild] = React.useState(
        props.summary.appStoreBuilds?.length > 0 ? props.summary.appStoreBuilds[0] : null,
    );

    const handleBuildSelect = (value) => {
        const matchedBuild = appStoreSelectedBuilds.find((build) => build.appStoreBuild.id === value);
        setSelectedBuild(matchedBuild);
    };

    return (
        <AppHomeCard
            appId={appIdParam}
            orgId={orgIdParam}
            releaseId={props.summary.releaseId}
            id={props.summary.id}
            displayableVersion={props.summary.displayableVersion}
            releaseName={props.summary.releaseName}
            borderColor={antGrey.grey5}
            headerComponent={
                <>
                    <Space
                        align="center"
                        split={<Divider type="vertical" style={{ minHeight: 26, backgroundColor: antGrey.grey5 }} />}
                        wrap
                    >
                        {tagDisplayAndColorForNextReleaseSummary(props.summary)}

                        {props.summary.type === ReleaseType.Rollback && <RollbackTag />}

                        {selectedBuild && (
                            <BuildSelectorComponent
                                appStoreSelectedBuilds={appStoreSelectedBuilds}
                                selectedBuild={selectedBuild}
                                handleBuildSelect={handleBuildSelect}
                            />
                        )}
                    </Space>
                </>
            }
            contentComponent={
                <>
                    <ReleaseDescriptionWidget description={props.summary.planningSummary} />
                    <ReleaseMetricsWidget
                        orgId={orgIdParam}
                        appId={appIdParam}
                        releaseId={props.summary.releaseId}
                        stats={props.summary.stats}
                    />
                    <ReleaseTimelineWidget
                        appId={appIdParam}
                        orgId={orgIdParam}
                        releaseId={props.summary.releaseId}
                        events={props.summary.events}
                        releaseType={props.summary.type}
                    />
                </>
            }
            sideComponent={
                <>
                    <TargetDatesWidget appId={appIdParam} summary={props.summary} />
                    <FeatureCompletionWidget featuresStep={props.summary.featuresStep} />
                    <Divider
                        style={{
                            margin: '16px 0',
                        }}
                    />
                    <ReleaseStepsWidget
                        steps={props.summary.steps}
                        platform={props.platform}
                        phasedRelease={props.summary.phasedRelease}
                        appStoreType={props.appStoreType}
                        shouldHideNonBlockingSteps={shouldHideNonBlockingSteps}
                        nonBlockingStepsConfigMap={nonBlockingStepsConfigMap}
                        releaseType={props.summary.type}
                        isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                    />
                    <Divider
                        style={{
                            margin: '16px 0',
                        }}
                    />
                    <ReleasePilotWidget
                        appId={appIdParam}
                        releasePilotId={props.summary.releasePilotId}
                        onReleaseOverview={false}
                    />
                </>
            }
        />
    );
};

export const tagDisplayAndColorForNextReleaseSummary = (summary: NextReleaseSummary): React.ReactNode => {
    let tagDisplayText = 'Next release';
    let durationText = null;

    const submissionStep = summary.steps.find((step) => step.type === ReleaseStepType.Submission);

    const isReadyForSubmission = submissionStep?.status === ReleaseStepStatus.Pending;

    const waitForReviewStat = summary.stats.find((stat) => stat.type === StatisticType.WaitingForReviewDuration);

    const waitingForReviewDurationText =
        waitForReviewStat ? dayjs.duration(waitForReviewStat.value, 'seconds').humanize() : null;

    const inReviewStat = summary.stats.find((stat) => stat.type === StatisticType.InReviewDuration);

    const inReviewDurationText = inReviewStat ? dayjs.duration(inReviewStat.value, 'seconds').humanize() : null;

    if (waitingForReviewDurationText) {
        tagDisplayText = 'Waiting for review';
        durationText = waitingForReviewDurationText;
    }

    if (inReviewDurationText) {
        tagDisplayText = 'In review';
        durationText = inReviewDurationText;
    }

    if (isReadyForSubmission) {
        tagDisplayText = 'Ready to submit';
        durationText = null;
    }

    return (
        <Space align="center">
            <Tag
                style={{
                    ...titleStyle,
                    fontSize: 11,
                    lineHeight: '22px',
                    fontWeight: 700,
                    color: runwayColors.statusYellow,
                    borderColor: runwayColors.statusYellow,
                }}
                color={runwayColors.white}
            >
                {tagDisplayText}
            </Tag>
            {durationText && (
                <Space align="center">
                    <ClockCircleOutlined style={{ color: antGrey.grey6 }} />
                    <Text style={{ fontSize: 14, color: antGrey.grey7 }}>{durationText}</Text>
                </Space>
            )}
        </Space>
    );
};
